import { DndContext, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { SortableContext } from "@dnd-kit/sortable";
import styled from "@emotion/styled";
import { FC, useState } from "react";
import { FieldArray } from "react-final-form-arrays";

import { DesignToken, SpacingToken } from "@livechat/design-system-react-components";

import Flex from "components/layout/Flex";
import { EventPlace, trackEngagement } from "helpers/analytics";
import { IFormBlock } from "store/entities/contact-forms/interfaces";
import { withVar } from "themes/helpers";

import { AddField } from "./AddFields";
import { FormBlock } from "./FormBlock";
import { SortableField } from "./SortableField";
import { LABEL_MAP } from "./constants";

export const Customize: FC = () => {
  const [openAccordion, setOpenAccordion] = useState<number | null>(null);
  const onAddField = (lastIndex: number): void => {
    setOpenAccordion(lastIndex);
  };
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 10,
      },
    })
  );

  return (
    <Flex column alignItems="center" padding={`${withVar(SpacingToken.Spacing12)} ${withVar(SpacingToken.Spacing16)}`}>
      <FieldArray<IFormBlock> name="fields">
        {({ fields }) => (
          <FieldsWrapper>
            {fields.value?.length > 0 ? (
              <DndContext
                sensors={sensors}
                onDragStart={() => {
                  setOpenAccordion(null);
                }}
                modifiers={[restrictToVerticalAxis]}
                onDragEnd={({ active, over }) => {
                  if (over && active.id !== over?.id) {
                    const activeIndex = active.data.current?.sortable?.index;
                    const overIndex = over.data.current?.sortable?.index;
                    if (activeIndex !== undefined && overIndex !== undefined) {
                      trackEngagement("Settings: Contact form field position changed", EventPlace.Settings);
                      fields.move(activeIndex, overIndex);
                    }
                  }
                }}
              >
                <SortableContext items={fields.value.map((_, index) => `item-${index}`)}>
                  {fields?.map((name, index) => {
                    const field = fields.value[index];
                    const fieldLabel = LABEL_MAP[field.type];
                    const fieldType = field.type;

                    if (fieldType === "options" || fieldType === "submit") {
                      return null;
                    }

                    return (
                      <SortableField
                        index={index}
                        key={`item-${fieldType}-${index}`}
                        fieldType={fieldType}
                        isOpen={openAccordion === index}
                        onOpen={() => setOpenAccordion(index)}
                        onClose={() => setOpenAccordion(null)}
                        fieldLabel={fieldLabel}
                        fieldName={name}
                        onRemove={fields.remove}
                      />
                    );
                  })}
                </SortableContext>
              </DndContext>
            ) : null}
            <AddField fields={fields} onAddField={() => onAddField(fields.value?.length - 2)} />
            <Divider />
            {fields.length !== undefined && (
              <>
                <FieldWrapper>
                  <FormBlock
                    fieldLabel="Send button"
                    fieldName={`${fields.name}.${fields.length - 2}`}
                    fieldType="submit"
                  />
                </FieldWrapper>
                <FieldWrapper>
                  <FormBlock
                    fieldLabel="More options"
                    fieldName={`${fields.name}.${fields.length - 1}`}
                    fieldType="options"
                  />
                </FieldWrapper>
              </>
            )}
          </FieldsWrapper>
        )}
      </FieldArray>
    </Flex>
  );
};

const FieldsWrapper = styled.div`
  margin-left: 19px;
`;

const FieldWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${withVar(SpacingToken.Spacing4)};
  margin-bottom: ${withVar(SpacingToken.Spacing2)};
  width: 482px;
`;

const Divider = styled.div`
  margin: 10px 0;
  border: 1px dashed ${withVar(DesignToken.BorderBasicPrimary)};
  max-width: 480px;
`;
